import React, { useEffect, useRef, useState } from 'react';
import { downloadFile, downloadFusionFile, formatDateToDDMMMYYYY, usePageTitle } from '../../services/UtilService';
import { useTranslation } from 'react-i18next';
import { Button, Divider, FormField, Icons, Select, Variants, Option, useFileXhrUploader, OnProgress, OnError, Card, Flexbox, Heading, Alert, Sentiments, Label, RangeDatePicker, TextInput, Table, THead, TH, TBody, TR, TD, Loading, Pagination, RangePickerValueType, Tooltip, Positions } from '@sede-enterprise/shell-ds-react-framework';
import { ContainerDiv, ErrorCell, NewCell, ProcessingCell, SuccessCell } from '../../components/styledComponent/StyledComponent';
import { Col, Row } from '../../components/flexbox/Flex';
import styled from 'styled-components';
import { postData, postDataFile } from '../../services/ApiService';
import { ApiUrls } from '../../helpers/ApiUrls';
import { useToast } from 'rc-toastr';

const blobName = 'Standard_Template';
const container  = 'filefusion';
const fileExtension  = '.xlsx';

export const FileUploaderContainer = styled(Flexbox)`
    height: 250px;
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
`;

const StyledHeading = styled(Heading)`
    color: #0097BB;
    margin-top: 15px;
    cursor: pointer;
    width: fit-content;
`;
export const BulkUploadHeadingContainer = styled.div`
    display: flex;
    justify-content: space-between;
    margin: 10px;   
`;
export const ClickandUploader = styled(Button)`
    color: #0097bb;
    cursor: pointer;
`;


interface FormData {
    airport: string | null,
	fuelingType: string | null
}

interface Transaction {
    fileId: string;
    fileName: string;
    uploadedBy: string;
    lastModified: string;
    distributionStatus: string;
    fileValidCheck : boolean;
    contentValidCheck : boolean;
    errorReason:string;

}

interface transactions  {
    data: Transaction[];
    count: number;
};

interface FusionFilesForm {
    "searchTerm": string,
    "limit": number,
    "offset": number,
    "dateFrom": string,
    "dateTo": string
}

const Overlay = styled.span`
    display: block;
    padding: 4px 8px;
    max-width: 240px;
  `;

const Admin = () => {
    const [transactions, setTransactions] = useState<transactions>({count: 0, data:[]});   
    const { t } = useTranslation();
    const [ formData, setFormData] = useState<FormData>({
        airport : '1',
        fuelingType: null
    });

    
    const [fussionFilesForm, setFussionFilesForm] = useState<FusionFilesForm>({
        "searchTerm": "",
        "limit": 10,
        "offset": 0,
        "dateFrom": "",
        "dateTo": ""
    });

    const [isFileUploading, setIsFileUploading] = useState<boolean>(false);
    const [fileName, setFileName] = useState<string>('');

    const [dateRange, setDateRange] = useState<RangePickerValueType>();
    const hiddenFileInput = useRef<HTMLInputElement>(null);

    const StandardAirportCode = '1';
    const airportsList = [{
        airportCode: StandardAirportCode,
        airportName: 'Standard'
    }];

    const fuelingTypesList = [{
        code: '1',
        value: 'ITP'
    }, {
        code: '2',
        value: 'Non ITP'
    }];

    const { toast } = useToast();

    usePageTitle(t('shellAviationAIMS')+" | Admin");

    const uploadFileContent = (file: File) => {
        const uploadURL = ApiUrls.fileFusionUpload;
        const validExtensions = ['.xlsx', '.xlsm', '.xlsb', '.xltx', '.xltm', '.xls', '.xlt'];
        if (!validExtensions.some(ext => file.name.endsWith(ext))) {
            toast.error("Invalid file format. Please upload a valid Excel file.");
            return;
        }

        const requestFileObject = {
            FormFile: file,
            SchemaType: formData.airport,
            TransactionTypeQualifier: formData.fuelingType,
            TransactionChannel: 1
        };
        setFileName(requestFileObject.FormFile.name);
        setIsFileUploading(true);

        postDataFile(uploadURL, requestFileObject).then((response)=>{
            setIsFileUploading(false);
            toast.success("File uploaded successfully");
            setFussionFilesForm({
                ...fussionFilesForm,
                offset: 0
            });
        }).catch((error)=>{
            setIsFileUploading(false);
        });
    };

    const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) {
            return;
        }
        uploadFileContent(file);
        e.target.value = '';
    };


    useEffect(() => {
        const url = ApiUrls.fileFusion;
        postData(url, fussionFilesForm).then((response) => {
            setTransactions(response.data);            
        });

    }, [fussionFilesForm]);

    const printStatus = (fileTransaction :Transaction) =>{
        switch (fileTransaction.distributionStatus) {
            case "New":
                return <NewCell>{fileTransaction.distributionStatus}</NewCell>;
            case "Success":
                return <SuccessCell>{fileTransaction.distributionStatus}</SuccessCell>;
            case "Failed":
                console.log(fileTransaction.errorReason)
                return (
                    <ErrorCell>
                        {fileTransaction.distributionStatus}
                        <span style={{ marginLeft: '8px' }}>
                            {!fileTransaction.fileValidCheck ? (
                                <Tooltip placement={Positions.Top} overlay={<Overlay>{fileTransaction.errorReason}</Overlay>} trigger={<Icons.InfoCircle style={{ cursor: 'pointer',fontSize: 20 }} />} />
                            ) : !fileTransaction.contentValidCheck ? ( 
                                <Icons.Download style={{ cursor: 'pointer' ,fontSize: 20 }} onClick={() => { downloadErrorFileContent(fileTransaction) }} />
                            ) : null}
                        </span>
                    </ErrorCell>
                );
            case "Processing":
                return <ProcessingCell>{fileTransaction.distributionStatus}</ProcessingCell>;
            default:
                return <span>{fileTransaction.distributionStatus}</span>; 
        }
    }

    const downloadStatus = (fileTransaction :Transaction) => {
        switch (fileTransaction.distributionStatus) {
            default:
                return <Flexbox justifyContent="center"><Icons.DownloadSolid style={{ cursor: 'pointer', fontSize: 25 }} onClick={() => {  downloadFileRow(fileTransaction)}} /></Flexbox>;
        }
    }

    
    const downloadErrorFileContent = (fileTransaction :Transaction) => {
        const url = ApiUrls.contentError + fileTransaction.fileId;
        downloadFusionFile(url,'Error_'+ fileTransaction.fileId+'.csv');
    }


    const attachmentClick = () =>{
        hiddenFileInput.current && hiddenFileInput.current.click();
    }

    const downloadFileRow = (fileTransaction :Transaction) => {
        const url = ApiUrls.fileFusionDownload + fileTransaction.fileId; 
        downloadFusionFile(url, fileTransaction.fileName);
    }

    const onPageChange = (current: number, size: number) => {
        setFussionFilesForm({
            ...fussionFilesForm,
            offset: (current - 1) * size,
            limit: size
        });
    }
    
    const onSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setFussionFilesForm({
            ...fussionFilesForm,
            searchTerm: e.target.value,
            offset: 0
        });
    }

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter') {
            setFussionFilesForm({
                ...fussionFilesForm,
                offset: 0
            });
        }
    };

    const onDateRangeChange = (dateValue: RangePickerValueType  ) => {
        setDateRange(dateValue);
        if(dateValue){
			const dateFrom:any = dateValue[0];
			const dateTo:any  = dateValue[1];

			const monthFrom: string = (dateFrom?.$M+1) < '10' ? '0'+(dateFrom?.$M+1) : (dateFrom?.$M+1);
			const monthTo: string = (dateTo.$M+1) < '10' ? '0'+(dateTo.$M+1) : (dateTo.$M+1);
			const dayFrom: string = dateFrom?.$D < 10 ? '0'+dateFrom?.$D : dateFrom?.$D;
			const dayTo: string = dateTo.$D < 10 ? '0'+dateTo.$D : dateTo.$D;
			const requestBody: FusionFilesForm  = {
				...fussionFilesForm,
				dateFrom: dateFrom.$y+'-'+ monthFrom +'-'+dayFrom,
				dateTo: dateTo.$y+'-'+ monthTo +'-'+dayTo,
				offset: 0
			};
			setFussionFilesForm(requestBody);
		}else{
			setFussionFilesForm({
                ...fussionFilesForm,
                dateFrom: '',
                dateTo: '',
                offset: 0
            });
		}
    } 

     const downloadTemplate = () =>{
            const url = ApiUrls.fileDownload + '/' + '0' + '/' + (blobName + fileExtension)+ '/'+ container;
            const fileName = blobName ?  blobName + fileExtension : 'temp';
            downloadFile(url, fileName);
        }


    const clearSearch = () =>{
        setDateRange(null);
        setFussionFilesForm({
            searchTerm: "",
            limit: 10,
            offset: 0,
            dateFrom: "",
            dateTo: ""
        });
    }


    return (
        <>
            <ContainerDiv>
                <Row>
                    <Col size="100">
                        <h2 style={{margin:'0px'}}>File Fusion</h2>
                    </Col>
                </Row>
            </ContainerDiv>

            <Card style={{ margin: 10, width: 'auto' }}>
                <ContainerDiv>
                    <Button variant={Variants.Outlined} onClick={()=>{downloadTemplate()}}><Icons.Download/> Download Template</Button>

                    
                </ContainerDiv>
                <Divider/>

                <ContainerDiv>
                    <Flexbox>
                        <Flexbox columnGap="10px" justifyContent="space-between">
                            <FormField id="airport" label="Airport" mandatory>
                                <Select value={formData?.airport} onChange={(value) => {setFormData({...formData, airport: value, fuelingType: value === StandardAirportCode ? null : formData.fuelingType}) }} >
                                    {airportsList.map((airport)=>(
                                        <Option value={airport.airportCode} key={airport.airportCode} selected={{}}>{airport.airportName}</Option>
                                    ))}
                                </Select>
                            </FormField>
                            
                            <FormField id="fuelingType" label="Fueling Type" mandatory>
                                <Select value={formData?.fuelingType} onChange={(value) => {setFormData({...formData, fuelingType: value}) }} disabled={formData.airport === StandardAirportCode} >
                                    {fuelingTypesList.map((fuelingType)=>(
                                        <Option value={fuelingType.code} key={fuelingType.code}>{fuelingType.value}</Option>
                                    ))}
                                </Select>
                            </FormField>

                            <FormField id="chooseFile" label="">

                                <StyledHeading type="h3">
                                    <Button disabled={ formData.airport === StandardAirportCode ? false : (formData.airport != undefined && formData.airport !== StandardAirportCode) && !formData.fuelingType ? false : true} onClick={()=>{attachmentClick()}} >Choose File</Button>
                                </StyledHeading>
                                
                            </FormField>
                        </Flexbox>
                    </Flexbox>
                </ContainerDiv>                

                <div>
                    <Flexbox columnGap="10px" style={{ padding: 20 }} justifyContent="space-between">
                        <Flexbox>
                            <input
                                type="file"
                                placeholder="Upload .xlsx or .csv"
                                accept=".xlsx,.csv"
                                ref={hiddenFileInput}
                                onChange={handleFileUpload}
                                style={{ display: 'none' }}
                            />
                        </Flexbox>
                    </Flexbox>

                    {isFileUploading && 
                        <Alert sentiment={Sentiments.Information} style={{marginBottom:'20px'}}>
                            <Icons.FileXlsOutline /> {fileName} <span style={{paddingLeft:'35%'}}> Uploading <Loading size='small'/> </span>
                        </Alert>
                    }                    
                </div>
                <Divider/>

                <Flexbox justifyContent="flex-end" style={{paddingTop:'20px'}} >      
                    <Row>
                        <Flexbox columnGap='4px'>
                            <Col size='45'>
                                <Row>
                                    <Flexbox columnGap='0px' style={{ alignItems:'center'}}>
                                        <Col size='25'>
                                            <Label bold>Date Range</Label>
                                        </Col>
                                        <Col size='75'>
                                            <RangeDatePicker placeholder={['Start date', 'Finish date']} onChange={(dateValue)=>{onDateRangeChange(dateValue)}} value={dateRange}/>                             
                                        </Col>
                                    </Flexbox>
                                </Row>
                            </Col>
                            <Col size='40'>
                                <Row>
                                    <Flexbox columnGap='0px' style={{ alignItems:'center'}}>
                                        <Col size='20'>
                                            <Label bold> Search</Label>
                                        </Col>
                                        <Col size='80'>
                                            <TextInput 
                                                size="medium" 
                                                onChange={(e) => {onSearchChange(e);}}
                                                suffix={{node: <Icons.Search />}} 
                                                placeholder="Enter the file name" 
                                                value={fussionFilesForm.searchTerm} 
                                                title="Search" 
                                                id="search"                                               
                                              />
                                        </Col>
                                    </Flexbox>
                                </Row>
                            </Col>
                            <Col size='15'>
                                <Flexbox columnGap='2px'>
                                    <Col size='80'>
                                        <Button variant="outlined" onClick={()=>{clearSearch()}}>
                                            <Icons.ArrowCcw/> 
                                            Refresh
                                        </Button>
                                    </Col>
                                </Flexbox>
                            </Col>
                        </Flexbox>
                    </Row>
                </Flexbox>


                <div style={{marginTop: '10px'}}>
                    <Row>
                        <Table>
                            <THead>
                                <TH>File ID</TH>
                                <TH>File Name</TH>
                                <TH>Uploaded By</TH>
                                <TH>Uploaded Date</TH>
                                <TH>Status</TH>
                                <TH>Download</TH>
                            </THead>
                            <TBody>

                                {transactions.data.map((file, index) => {
                                    return (
                                        <TR key={index}>
                                            <TD>{file.fileId}</TD>
                                            <TD>{file.fileName}</TD>
                                            <TD>{file.uploadedBy}</TD>
                                            <TD>{formatDateToDDMMMYYYY(file.lastModified)}</TD>
                                            <TD>{printStatus(file)}</TD>
                                            <TD>{downloadStatus(file)}</TD>                                            
                                        </TR>
                                    );
                                })}

                            </TBody>
                        </Table>
                    </Row>
                    <Row>
                        <Pagination total={transactions.count} current={fussionFilesForm.offset ? (fussionFilesForm.offset/fussionFilesForm.limit) + 1: 1} onChange={(pageNo: number, size: number) => {onPageChange(pageNo, size)}} pageSize={fussionFilesForm.limit} showSizeChanger />
                    </Row>
                </div>
            </Card>
        </>
    )
}
export default Admin;